/**
 * plugins/versionCheck.js
 *
 * Removes the "hard refresh after every deploy" tax. The build emits
 * /version.json; this poller compares it with the version baked into the
 * running bundle, and when they diverge `isUpdateAvailable` flips true so
 * UpdateBanner can offer a one-click refresh.
 *
 * Polls every 5 minutes and immediately when the tab regains focus or
 * visibility. Network failures are silent - a missed check never blocks
 * the UI, and the next visibility transition retries.
 */
import { ref } from 'vue';
import { version as currentVersion } from '../../package.json';

const POLL_INTERVAL_MS = 5 * 60_000;

export const isUpdateAvailable = ref(false);
export const latestVersion = ref(currentVersion);

let started = false;

async function checkOnce() {
  try {
    const res = await fetch('/version.json', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return;
    const data = await res.json();
    if (typeof data?.version !== 'string') return;
    latestVersion.value = data.version;
    if (data.version !== currentVersion) {
      isUpdateAvailable.value = true;
    }
  } catch {
    // Offline / CDN blip / aggressive ad-blocker. Try again on next tick.
  }
}

export function startVersionCheck() {
  if (started || typeof window === 'undefined') return;
  started = true;

  // First check shortly after mount so a stale tab opened during a
  // deploy gets the prompt within seconds - not on the next 5-min tick.
  setTimeout(checkOnce, 8_000);

  setInterval(checkOnce, POLL_INTERVAL_MS);

  window.addEventListener('focus', checkOnce);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkOnce();
  });
}

export function applyUpdate() {
  // Drop the persisted Query cache before reloading so the new bundle
  // doesn't hydrate stale-schema entries from the old bundle.
  try {
    window.localStorage.removeItem('esteps-doctor-query-cache');
  } catch {
    // SecurityError under strict storage policies - reload anyway.
  }
  window.location.reload();
}
